import { Popover } from '@material-ui/core';
import CSS from 'csstype';
import React, { useEffect, useState } from 'react';
import { FixedSizeList } from 'react-window';

import { VirtualRow } from '~/src/dash/components/dropdowns/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  borderRadius,
  Col,
  Cols,
  ISimpleOptionSelect,
  Row,
  Rows,
  spacingV2,
  Text,
  TextButton,
  TextInput,
} from '~/src/dash/publicaUI';
import { IListItem } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { plural } from '~/src/dash/utils/plural';

export interface ISimpleDropdownOptionSelect extends ISimpleOptionSelect {
  active?: boolean;
}

interface ISimpleDropdownProps {
  options: ISimpleDropdownOptionSelect[];
  maxNumberSelected?: number;
  label?: string;
  isSelectionRequired?: boolean;
  style?: CSS.Properties;
  isIDDisplayed?: boolean;
  isHidingInactive?: boolean;
  isSelectAllEnabled?: boolean;
  onChange: (ids: string[]) => void;
  onClear: () => void;
  onSearchUpdate?: (search: string) => void;
}

export const SimpleDropdown = ({
  label,
  options,
  isSelectionRequired,
  style,
  maxNumberSelected,
  isIDDisplayed,
  isHidingInactive,
  isSelectAllEnabled,
  onChange,
  onClear,
  onSearchUpdate,
}: ISimpleDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null);
  const [displayedOptions, setDisplayedOptions] = useState<ISimpleDropdownOptionSelect[]>(options);
  const [search, setSearch] = useState('');
  const [isSortedAlphabeticallyAscending, setIsSortedAlphabeticallyAscending] = useState(false);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const selectedCount = options.filter((item) => item.isSelected).length;

  useEffect(() => {
    if (options) {
      setDisplayedOptions(getDisplayedOptions(search, options));
    }
  }, [options]);

  useEffect(() => {
    setDisplayedOptions(getDisplayedOptions(search, options));
    if (!!onSearchUpdate) {
      onSearchUpdate(search);
    }
  }, [search]);

  const getSortText = (): string => {
    return !isSortedAlphabeticallyAscending ? 'A-Z' : 'Z-A';
  };

  const getHeight = (numItems: number) => {
    if (numItems >= 12) return 360;
    return numItems * 30 + 4;
  };

  const filterInactiveOptions = (
    list: ISimpleDropdownOptionSelect[],
  ): ISimpleDropdownOptionSelect[] => {
    return isHidingInactive ? list.filter((item) => item.active) : list;
  };

  const queryList = (
    query: string,
    list: ISimpleDropdownOptionSelect[],
  ): ISimpleDropdownOptionSelect[] => {
    if (!query) {
      return list;
    }

    const casedQuery = query.toLowerCase();
    return list.filter(
      (item) => item.key.includes(query) || item.label.toLowerCase().includes(casedQuery),
    );
  };

  const getDisplayedOptions = (
    search: string,
    options: ISimpleDropdownOptionSelect[],
  ): ISimpleDropdownOptionSelect[] => {
    return queryList(search, filterInactiveOptions(options));
  };

  const getPlaceholderText = (sortedList: ISimpleDropdownOptionSelect[]): string => {
    if (selectedCount === 0) {
      return `Select ${maxNumberSelected === 1 ? label : plural(label || '')}`;
    } else if (selectedCount === 1) {
      return `${sortedList[0].label}`;
    }
    return `${selectedCount} ${plural(label || '')} Selected`;
  };

  const handleOpenDropdown = (e: React.MouseEvent) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseDropdown = () => {
    setSearch('');
    setAnchorEl(null);
  };

  const handleChange = (id: string) => {
    const idx = options.findIndex((item) => item.key === id);
    if (idx === -1) {
      return;
    }
    if (options[idx].isSelected) {
      if (isSelectionRequired && selectedCount === 1) return;
      options[idx].isSelected = false;
    } else if (maxNumberSelected && selectedCount >= maxNumberSelected) {
      const selectedIdx = options.findIndex((option) => option.isSelected);
      if (selectedIdx) {
        options[selectedIdx].isSelected = false;
      }
    } else {
      options[idx].isSelected = true;
    }
    const selectedIDs = options.filter((option) => option.isSelected).map((option) => option.key);
    onChange(selectedIDs);
  };

  const handleSelectAll = () => {
    onChange(displayedOptions.map((option) => option.key));
  };

  const sortOptions = (
    displayedOptions: ISimpleDropdownOptionSelect[],
  ): ISimpleDropdownOptionSelect[] => {
    const displayedOptionsCopy = [...displayedOptions];
    if (!isSortedAlphabeticallyAscending) {
      displayedOptionsCopy.sort((a, b) => {
        if (a.isSelected === b.isSelected) {
          return a.label.localeCompare(b.label);
        } else if (a.isSelected !== b.isSelected) {
          return a.isSelected > b.isSelected ? -1 : 1;
        } else {
          return 0;
        }
      });
    } else {
      displayedOptionsCopy.sort((a, b) => {
        if (a.isSelected === b.isSelected) {
          return b.label.localeCompare(a.label);
        } else if (a.isSelected !== b.isSelected) {
          return a.isSelected > b.isSelected ? -1 : 1;
        } else {
          return 0;
        }
      });
    }
    return displayedOptionsCopy;
  };

  const convertDropdownSelectOptionsToListItems = (
    items: ISimpleDropdownOptionSelect[],
  ): IListItem[] => {
    return items.map((item) => {
      return {
        ID: item.key,
        title: item.label,
        selected: item.isSelected,
      };
    });
  };

  return (
    <>
      <Row
        displayFlex
        onClick={handleOpenDropdown}
        extraSmallVerticalPaddingV2
        smallHorizontalPaddingV2
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          border: `1px solid ${publicaColors.lightGray}`,
          borderRadius: `${borderRadius.small}`,
          background: 'inherit',
          ...style,
        }}
      >
        <Text small style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
          {getPlaceholderText(sortOptions(options))}
        </Text>
        <IconV2 name='caret' color={publicaColors.gray} />
      </Row>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseDropdown}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            overflow: 'hidden',
            width: '30rem',
            border: `1px solid ${publicaColors.lightGray}`,
            boxShadow: 'none',
          },
        }}
      >
        <Rows>
          <Row smallPadding style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
            <TextInput
              enableClear
              fullWidth
              handleOnChange={(value: string) => setSearch(value)}
              inputSize='small'
              isAutoFocused
              msDelay={500}
              placeholder={`Search`}
              prefix='search'
            />
            <Row smallHorizontalPaddingV2 smallVerticalPaddingV2 paddingBottom='0px'>
              <Cols
                gap={spacingV2.base}
                style={{ justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Col>
                  <Cols gap={spacingV2.base}>
                    {isSelectAllEnabled && selectedCount < options.length && (
                      <Col displayFlex style={{ alignItems: 'center' }}>
                        <TextButton size='small' onClick={handleSelectAll}>
                          Select All
                        </TextButton>
                      </Col>
                    )}

                    <Col>
                      <Text muted small disableSelect>
                        {maxNumberSelected
                          ? `${selectedCount} /${maxNumberSelected} Selected`
                          : `${selectedCount} Selected`}
                      </Text>
                    </Col>

                    {!isSelectionRequired && selectedCount > 0 && (
                      <Col displayFlex style={{ alignItems: 'center' }}>
                        <TextButton size='small' onClick={onClear}>
                          Clear
                        </TextButton>
                      </Col>
                    )}
                  </Cols>
                </Col>
                <Col>
                  <TextButton
                    size='small'
                    onClick={() =>
                      setIsSortedAlphabeticallyAscending(!isSortedAlphabeticallyAscending)
                    }
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <IconV2
                      name='arrow'
                      color={publicaColors.lightBlue}
                      style={{ transform: isSortedAlphabeticallyAscending ? 'scaleY(-1)' : 'none' }}
                    />
                    {getSortText()}
                  </TextButton>
                </Col>
              </Cols>
            </Row>
          </Row>
          <Row smallPadding>
            <FixedSizeList
              height={getHeight(displayedOptions.length)}
              itemCount={displayedOptions.length}
              itemSize={30}
              width={480}
            >
              {({ index, style }) =>
                VirtualRow({
                  index,
                  displayID: isIDDisplayed,
                  style,
                  handleChange,
                  options: convertDropdownSelectOptionsToListItems(sortOptions(displayedOptions)),
                })
              }
            </FixedSizeList>
          </Row>
        </Rows>
      </Popover>
    </>
  );
};
